import { useState, useEffect, useCallback, useRef } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

export const useTicketsList = (filterTab = 'todos', idUsuarioAsignado = null, enabled = true, initialPageSize = 20, onInitialLoaded = null) => {
  // Caché de tickets por pestaña, usuario y página: { [tab_userId_pX_sY]: [...] }
  const [tabCache, setTabCache] = useState({});
  const [counts, setCounts] = useState({ todos: 0, pendientes: 0, enProgreso: 0, cerrados: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useToken();
  const { showErrorToast } = useToast();

  const cacheRef = useRef({});
  const inFlightRef = useRef(new Set());
  const toastRef = useRef(showErrorToast);
  toastRef.current = showErrorToast;
  const recentEventsRef = useRef(new Map());
  const refreshCountsTimeoutRef = useRef(null);

  const activeTabRef = useRef(filterTab);
  activeTabRef.current = filterTab;
  const activeAgentRef = useRef(idUsuarioAsignado);
  activeAgentRef.current = idUsuarioAsignado;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const onInitialLoadedRef = useRef(onInitialLoaded);
  onInitialLoadedRef.current = onInitialLoaded;
  const initialLoadedFiredRef = useRef(false);
  const notifyInitialLoaded = () => {
    if (!initialLoadedFiredRef.current && onInitialLoadedRef.current) {
      initialLoadedFiredRef.current = true;
      onInitialLoadedRef.current();
    }
  };

  const getEstadoParam = tab => {
    if (tab === 'pendientes') return 1;
    if (tab === 'en_progreso') return 3;
    if (tab === 'cerrados') return 2;
    return null; // 'todos'
  };

  const getTabByEstado = estado => {
    if (estado === 1) return 'pendientes';
    if (estado === 3) return 'en_progreso';
    if (estado === 2) return 'cerrados';
    return 'todos';
  };

  const getReadTicketsMap = () => {
    try {
      const stored = localStorage.getItem('support_engine_read_tickets');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const markTicketAsRead = useCallback((idTicket) => {
    if (!idTicket) return;
    const numId = Number(idTicket);
    try {
      const currentMap = getReadTicketsMap();
      currentMap[numId] = new Date().toISOString();
      localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
    } catch (e) {
      console.warn('Error al guardar ticket leido en localStorage:', e);
    }

    setTabCache(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (Array.isArray(updated[key])) {
          updated[key] = updated[key].map(t =>
            Number(t.idTicket) === numId ? { ...t, bSinLeer: false } : t
          );
        }
      });
      return updated;
    });

    Object.keys(cacheRef.current).forEach(key => {
      if (Array.isArray(cacheRef.current[key])) {
        cacheRef.current[key] = cacheRef.current[key].map(t =>
          Number(t.idTicket) === numId ? { ...t, bSinLeer: false } : t
        );
      }
    });
  }, []);

  const invalidateTabCache = useCallback((tabName) => {
    Object.keys(cacheRef.current).forEach(k => {
      if (k.startsWith(`${tabName}_`) || k.startsWith('todos_')) {
        delete cacheRef.current[k];
      }
    });
    setTabCache(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (k.startsWith(`${tabName}_`) || k.startsWith('todos_')) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const fetchTicketsForTab = useCallback(async (tab, pageNum = 1, sizeNum = 20, force = false) => {
    if (!token) return;

    const hasAgentFilter = activeAgentRef.current && activeAgentRef.current !== 'todos' && activeAgentRef.current !== 'all';
    const cacheKey = `${tab}_${hasAgentFilter ? activeAgentRef.current : 'all'}_p${pageNum}_s${sizeNum}`;

    // Si ya está en curso una petición idéntica para esta clave, no duplicar
    if (inFlightRef.current.has(cacheKey)) {
      return;
    }

    // Si ya tenemos los datos en caché y no es recarga forzada:
    // Mostramos los datos de inmediato para evitar trabas o saltos visuales (0ms)
    const cachedData = cacheRef.current[cacheKey];
    if (cachedData && !force) {
      const total = cachedData.length > 0 && cachedData[0].iTotalRegistros != null
        ? Number(cachedData[0].iTotalRegistros)
        : cachedData.length;
      setTotalRegistros(total);
      notifyInitialLoaded();
      // No activamos el spinner bloqueante si ya tenemos datos para mostrar
    } else {
      setIsLoading(true);
    }

    inFlightRef.current.add(cacheKey);
    setError(null);

    try {
      const estado = getEstadoParam(tab);
      const params = new URLSearchParams();
      if (estado) params.append('iEstadoTicket', estado);
      if (hasAgentFilter) params.append('idUsuarioAsignado', activeAgentRef.current);
      params.append('pageNumber', pageNum);
      params.append('pageSize', sizeNum);

      const url = `/api/SupportEngine/GetTicketsList?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (!response.ok) {
        let errorDetails = '';
        try {
          const errJson = await response.json();
          errorDetails = errJson.message || errJson.details || JSON.stringify(errJson);
        } catch {
          errorDetails = await response.text();
        }
        throw new Error(`Error ${response.status}: ${errorDetails || 'No se pudieron cargar los tickets'}`);
      }

      const result = await response.json();
      const data = result.data || result.tickets || (Array.isArray(result) ? result : []);
      const ticketList = Array.isArray(data) ? data : [];

      const readMap = getReadTicketsMap();
      const enrichedTicketList = ticketList.map(t => {
        const id = Number(t.idTicket);
        const lastRead = readMap[id];
        // Es sin leer solo si es un ticket en progreso con actividad posterior a la última lectura o sin respuesta inicial
        const isClientUnread = Number(t.iEstadoTicket) === 3 && (lastRead
          ? new Date(t.dUltimaActividad || t.dCreatedDate) > new Date(lastRead)
          : !t.dFechaRespuestaInicial);

        return {
          ...t,
          bSinLeer: t.bSinLeer !== undefined ? t.bSinLeer : isClientUnread
        };
      });

      // Guardar en la caché bajo la clave combinada de página y tamaño
      cacheRef.current[cacheKey] = enrichedTicketList;
      setTabCache(prev => ({
        ...prev,
        [cacheKey]: enrichedTicketList
      }));

      // Total de registros desde la BD (retornado por el SP en iTotalRegistros) o longitud de la lista
      const totalCount = ticketList.length > 0 && ticketList[0].iTotalRegistros != null
        ? Number(ticketList[0].iTotalRegistros)
        : ticketList.length;

      setTotalRegistros(totalCount);

      const tabKeyMap = {
        todos: 'todos',
        pendientes: 'pendientes',
        en_progreso: 'enProgreso',
        cerrados: 'cerrados'
      };
      const currentTabKey = tabKeyMap[tab];
      if (currentTabKey) {
        setCounts(prev => ({
          ...prev,
          [currentTabKey]: totalCount
        }));
      }

      // Actualizar conteos directamente desde la respuesta de GetTicketsList si vienen (3er SELECT)
      const countsData = result.counts || (result.data && !Array.isArray(result.data) ? result.data.counts : null);
      if (countsData) {
        setCounts({
          todos: Number(countsData.iTotalTodos ?? countsData.totalTodos ?? 0),
          pendientes: Number(countsData.iTotalPendientes ?? countsData.totalPendientes ?? 0),
          enProgreso: Number(countsData.iTotalEnProgreso ?? countsData.totalEnProgreso ?? 0),
          cerrados: Number(countsData.iTotalCerrados ?? countsData.totalCerrados ?? 0)
        });
      }
    } catch (err) {
      setError(err.message || 'Error al obtener tickets');
      if (toastRef.current) {
        toastRef.current('Error', 'No se pudieron sincronizar los tickets de soporte');
      }
    } finally {
      inFlightRef.current.delete(cacheKey);
      setIsLoading(false);
      notifyInitialLoaded();
    }
  }, [token]);

  const countsRef = useRef(counts);
  countsRef.current = counts;

  // Un solo efecto para controlar cambios de pestaña, usuario asignado o página sin carreras paralelas
  useEffect(() => {
    if (!enabled) {
      cacheRef.current = {};
      return;
    }

    const isFilterOrAgentChange =
      activeTabRef.current !== filterTab || activeAgentRef.current !== idUsuarioAsignado;

    activeTabRef.current = filterTab;
    activeAgentRef.current = idUsuarioAsignado;

    if (isFilterOrAgentChange) {
      const currentCounts = countsRef.current;
      const targetCount = filterTab === 'pendientes'
        ? currentCounts.pendientes
        : filterTab === 'en_progreso'
        ? currentCounts.enProgreso
        : filterTab === 'cerrados'
        ? currentCounts.cerrados
        : currentCounts.todos;
      setTotalRegistros(targetCount ?? 0);

      // Limpiar caché previa de esta pestaña al cambiar de tab para evitar mostrar datos viejos
      const hasAgent = idUsuarioAsignado && idUsuarioAsignado !== 'todos' && idUsuarioAsignado !== 'all';
      const newCacheKey = `${filterTab}_${hasAgent ? idUsuarioAsignado : 'all'}_p1_s${pageSize}`;
      delete cacheRef.current[newCacheKey];
      setTabCache(prev => {
        if (!prev[newCacheKey]) return prev;
        const next = { ...prev };
        delete next[newCacheKey];
        return next;
      });

      if (page !== 1) {
        setPage(1);
        // Al resetear page a 1, el siguiente ciclo ejecutará este efecto con la página 1
        return;
      }
    }

    fetchTicketsForTab(filterTab, page, pageSize, isFilterOrAgentChange);
  }, [filterTab, idUsuarioAsignado, page, pageSize, fetchTicketsForTab, enabled]);

  const reloadTickets = useCallback(() => {
    if (!enabled) return;
    cacheRef.current = {};
    setTabCache({});
    fetchTicketsForTab(activeTabRef.current, pageRef.current, pageSizeRef.current, true);
  }, [fetchTicketsForTab, enabled]);

  // Refresco de contadores reales directamente desde el SP del servidor (una sola fuente de verdad)
  const refreshCounts = useCallback(async () => {
    if (!enabled || !token) return;
    try {
      const hasAgentFilter = activeAgentRef.current && activeAgentRef.current !== 'todos' && activeAgentRef.current !== 'all';
      const params = new URLSearchParams();
      if (hasAgentFilter) params.append('idUsuarioAsignado', activeAgentRef.current);
      params.append('pageNumber', 1);
      params.append('pageSize', 1);

      const resp = await fetch(`/api/SupportEngine/GetTicketsList?${params.toString()}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const countsData = result.counts || (result.data && !Array.isArray(result.data) ? result.data.counts : null);
        if (countsData) {
          setCounts({
            todos: Number(countsData.iTotalTodos ?? countsData.totalTodos ?? 0),
            pendientes: Number(countsData.iTotalPendientes ?? countsData.totalPendientes ?? 0),
            enProgreso: Number(countsData.iTotalEnProgreso ?? countsData.totalEnProgreso ?? 0),
            cerrados: Number(countsData.iTotalCerrados ?? countsData.totalCerrados ?? 0)
          });
        }
      }
    } catch (e) {
      console.warn('Error al refrescar conteos de tickets:', e);
    }
  }, [enabled, token]);

  const triggerRefreshCounts = useCallback(() => {
    if (refreshCountsTimeoutRef.current) {
      clearTimeout(refreshCountsTimeoutRef.current);
    }
    refreshCountsTimeoutRef.current = setTimeout(() => {
      refreshCounts();
    }, 300);
  }, [refreshCounts]);

  useEffect(() => {
    return () => {
      if (refreshCountsTimeoutRef.current) {
        clearTimeout(refreshCountsTimeoutRef.current);
      }
    };
  }, []);

  // Manejo reactivo de actividad en tickets (mensajes entrantes / salientes) estilo WhatsApp
  const handleTicketActivity = useCallback(async (event) => {
    if (!enabled || !token) return;
    const detail = event?.detail;
    const idTicket = Number(detail?.idTicket);
    if (!idTicket) return;

    // 1. Deduplicación por idTicket: ignorar eventos idénticos que lleguen en menos de 1.5s (SignalR + FCM)
    const now = Date.now();
    const lastEventTime = recentEventsRef.current.get(idTicket);
    if (lastEventTime && now - lastEventTime < 1500) {
      return;
    }
    recentEventsRef.current.set(idTicket, now);
    if (recentEventsRef.current.size > 50) {
      for (const [k, time] of recentEventsRef.current.entries()) {
        if (now - time > 5000) recentEventsRef.current.delete(k);
      }
    }

    console.log('📋 [useTicketsList] Actividad de ticket detectada:', detail);

    const currentTab = activeTabRef.current;
    const currentAgent = activeAgentRef.current;
    const hasAgent = currentAgent && currentAgent !== 'todos' && currentAgent !== 'all';
    const currentPage = pageRef.current;
    const currentSize = pageSizeRef.current;
    const currentCacheKey = `${currentTab}_${hasAgent ? currentAgent : 'all'}_p${currentPage}_s${currentSize}`;
    const list = cacheRef.current[currentCacheKey] || [];

    const existingIndex = list.findIndex(t => Number(t.idTicket) === idTicket);
    const isClientMessage = detail.bEsAgente === 0 || detail.bEsAgente === false || detail.isAgent === false;
    const isCurrentlyOpen = Boolean(detail.isCurrentlyOpen);

    if (existingIndex !== -1) {
      // Caso 1: El ticket ya existe en la lista del estado actual
      const current = list[existingIndex];
      const newStatus = detail.iEstadoTicket != null ? Number(detail.iEstadoTicket) : null;
      const expectedStatus = getEstadoParam(currentTab);

      // Si el ticket cambió de estado (ej. pasó de pendiente a en progreso), removerlo de esta pestaña
      if (newStatus != null && expectedStatus != null && newStatus !== expectedStatus) {
        const remaining = list.filter((_, idx) => idx !== existingIndex);
        cacheRef.current[currentCacheKey] = remaining;
        setTabCache(prev => ({ ...prev, [currentCacheKey]: remaining }));
        setTotalRegistros(prev => Math.max(0, prev - 1));

        invalidateTabCache(getTabByEstado(newStatus));
        triggerRefreshCounts();
        return;
      }

      const shouldMarkUnread = isClientMessage && !isCurrentlyOpen && currentTab !== 'pendientes';

      const updated = {
        ...current,
        iEstadoTicket: newStatus !== null ? newStatus : current.iEstadoTicket,
        dUltimaActividad: detail.dUltimaActividad || new Date().toISOString(),
        vTextoMensaje: detail.vTextoMensaje ? detail.vTextoMensaje : current.vUltimoMensaje,
        vUltimoMensaje: detail.vTextoMensaje ? detail.vTextoMensaje : current.vUltimoMensaje,
        dFechaRespuestaInicial: detail.dFechaRespuestaInicial !== undefined ? detail.dFechaRespuestaInicial : current.dFechaRespuestaInicial,
        iTiempoRespuestaMinutos: detail.iTiempoRespuestaMinutos !== undefined ? detail.iTiempoRespuestaMinutos : current.iTiempoRespuestaMinutos,
        bSinLeer: isCurrentlyOpen ? false : (shouldMarkUnread ? true : current.bSinLeer)
      };

      if (shouldMarkUnread) {
        try {
          const currentMap = getReadTicketsMap();
          delete currentMap[idTicket];
          localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
        } catch (e) {
          console.warn('Error al actualizar ticket leido en localStorage:', e);
        }
      } else if (isCurrentlyOpen) {
        try {
          const currentMap = getReadTicketsMap();
          currentMap[idTicket] = new Date().toISOString();
          localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
        } catch (e) {
          console.warn('Error al actualizar ticket leido en localStorage:', e);
        }
      }

      let reordered;
      if (currentTab === 'pendientes') {
        // En la cola de pendientes: se actualiza en su lugar (mantiene su orden por fecha)
        reordered = [...list];
        reordered[existingIndex] = updated;
      } else {
        // En progreso / otros: mover a la cima (índice 0) como WhatsApp
        const remaining = list.filter((_, idx) => idx !== existingIndex);
        reordered = [updated, ...remaining];
      }

      cacheRef.current[currentCacheKey] = reordered;
      setTabCache(prev => ({
        ...prev,
        [currentCacheKey]: reordered
      }));
    } else {
      // Caso 2: El ticket no está cargado en el state actual. Consultar solo ese ticket
      try {
        const resp = await fetch(`/api/SupportEngine/GetTicketDetail/${idTicket}`, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });

        if (resp.ok) {
          const resJson = await resp.json();
          const d = resJson.data || resJson;
          if (!d || !d.idTicket) return;

          const ticketEstado = Number(d.iEstadoTicket);
          const estadoActual = getEstadoParam(currentTab);
          const matchesTab = estadoActual === null || ticketEstado === estadoActual;
          const isUnassignedPending = ticketEstado === 1 && !d.idUsuarioAsignado;
          const matchesAgent = !hasAgent || Number(d.idUsuarioAsignado) === Number(currentAgent) || isUnassignedPending;

          if (matchesTab && matchesAgent) {
            const newTicket = {
              idTicket: d.idTicket,
              iEstadoTicket: d.iEstadoTicket,
              dUltimaActividad: detail.dUltimaActividad || d.dUltimaActividad || new Date().toISOString(),
              dCreatedDate: d.dCreatedDate,
              idClienteAkasia: d.idClienteAkasia,
              vTelefono: d.vTelefono,
              idUsuarioAsignado: d.idUsuarioAsignado,
              vNombreUsuarioAsignado: d.vNombreUsuarioAsignado,
              vAsunto: d.vAsunto,
              vUltimoMensaje: detail.vTextoMensaje || d.vUltimoMensaje || '',
              dFechaRespuestaInicial: d.dFechaRespuestaInicial,
              iTiempoRespuestaMinutos: d.iTiempoRespuestaMinutos,
              vCustomerName: d.vCustomerName,
              bSinLeer: isCurrentlyOpen ? false : (isClientMessage && currentTab !== 'pendientes')
            };

            if (isClientMessage && !isCurrentlyOpen) {
              try {
                const currentMap = getReadTicketsMap();
                delete currentMap[idTicket];
                localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
              } catch (e) {
                console.warn('Error al actualizar ticket leido en localStorage:', e);
              }
            } else if (isCurrentlyOpen) {
              try {
                const currentMap = getReadTicketsMap();
                currentMap[idTicket] = new Date().toISOString();
                localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
              } catch (e) {
                console.warn('Error al actualizar ticket leido en localStorage:', e);
              }
            }

            const currentTabList = cacheRef.current[currentCacheKey] || [];
            let newList;
            if (currentTab === 'pendientes') {
              // En cola de pendientes entra ordenado por antigüedad (dCreatedDate)
              newList = [...currentTabList.filter(t => Number(t.idTicket) !== idTicket), newTicket]
                .sort((a, b) => new Date(a.dCreatedDate) - new Date(b.dCreatedDate));
            } else {
              // En progreso va a la cima
              newList = [newTicket, ...currentTabList.filter(t => Number(t.idTicket) !== idTicket)];
            }

            if (newList.length > currentTabList.length) {
              setTotalRegistros(prev => prev + 1);
            }

            cacheRef.current[currentCacheKey] = newList;
            setTabCache(prev => ({
              ...prev,
              [currentCacheKey]: newList
            }));

            // Sincronizar conteos reales desde el servidor
            triggerRefreshCounts();
          } else {
            // El ticket pertenece a OTRA pestaña (ej. estamos en 'en_progreso' y el ticket es 'pendientes'):
            // 1. Invalidar la caché de esa otra pestaña para que al hacer clic traiga los datos frescos del servidor
            invalidateTabCache(getTabByEstado(ticketEstado));
            // 2. Refrescar los contadores reales desde el servidor de forma precisa
            triggerRefreshCounts();
          }
        }
      } catch (err) {
        console.error('Error al sincronizar ticket activo:', err);
      }
    }
  }, [enabled, token, invalidateTabCache, triggerRefreshCounts]);

  // Listener local de eventos DOM en ventana (estable, no se remueve en cada cambio de tab)
  useEffect(() => {
    window.addEventListener('akasia:support:ticket_activity', handleTicketActivity);
    return () => {
      window.removeEventListener('akasia:support:ticket_activity', handleTicketActivity);
    };
  }, [handleTicketActivity]);

  // Listener para eventos de lectura cuando el agente abre el ticket o responde
  useEffect(() => {
    const handleExternalRead = e => {
      const idTicket = Number(e?.detail?.idTicket);
      if (idTicket) {
        markTicketAsRead(idTicket);
      }
    };
    window.addEventListener('akasia:support:ticket_read', handleExternalRead);
    return () => {
      window.removeEventListener('akasia:support:ticket_read', handleExternalRead);
    };
  }, [markTicketAsRead]);

  const hasAgentFilter = idUsuarioAsignado && idUsuarioAsignado !== 'todos' && idUsuarioAsignado !== 'all';
  const currentCacheKey = `${filterTab}_${hasAgentFilter ? idUsuarioAsignado : 'all'}_p${page}_s${pageSize}`;
  const currentTickets = tabCache[currentCacheKey] || cacheRef.current[currentCacheKey] || [];
  const totalPages = Math.max(1, Math.ceil(totalRegistros / pageSize));

  return {
    tickets: currentTickets,
    counts,
    isLoading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalRegistros,
    totalPages,
    reloadTickets,
    markTicketAsRead
  };
};

export default useTicketsList;

